import styled, { css } from 'styled-components';
import { media } from 'src/styles/designs';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { EventAgendaTimeslot } from 'src/modules/events/models';
import { useTranslate } from 'react-t7e';
import { Button } from 'src/components/common/Button';
import { EventPageTicketPopup } from 'src/modules/events/components/EventPage/EventPageAgendaSection/EventPageTicketPopup';
import { useState } from 'react';
import { Checkbox } from 'src/components/common/Checkbox';
import { isPast } from 'date-fns';

export interface EventPageAgendaTimeslotDetailsProps {
  timeslot: EventAgendaTimeslot;
  isChecked: boolean;
  toggleIsChecked: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: auto min-content;
  gap: 20px;
`;

const Action = styled.div`
  align-self: flex-start;
  min-width: min-content;

  .btn {
    min-width: 0;
  }

  ${media.w.lessThan.desktop} {
    margin-right: -20px;
  }
`;

const Heading = styled.h3`
  font-weight: 300;
  font-size: 18px;
  line-height: 24px;
  overflow-wrap: break-word;
  text-transform: uppercase;

  ${media.w.lessThan.desktop} {
    font-size: 14px;
    line-height: 19px;
  }
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Detail = styled.span<{ $variant?: 'primary' | 'secondary' }>`
  font-size: 12px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text.palette.secondary};

  &:not(:last-child):after {
    content: '';
    display: inline-block;
    width: 4px;
    height: 4px;
    vertical-align: middle;
    line-height: 20px;
    border-radius: 50%;
    background: ${({ theme }) => theme.skeleton.base};
    margin: 0 8px;
  }

  ${({ $variant }) => {
    switch ($variant) {
      case 'primary':
        return css`
          font-weight: 600;
          color: ${({ theme }) => theme.palette.success};
        `;
      case 'secondary':
        return css`
          font-weight: 600;
          color: ${({ theme }) => theme.text.palette.accent};
        `;
    }
  }}
`;

export const EventPageAgendaTimeslotDetails = ({
  timeslot,
  toggleIsChecked,
  isChecked,
}: EventPageAgendaTimeslotDetailsProps) => {
  const [popupVisible, setPopupVisible] = useState(false);
  const isInPast = isPast(new Date(timeslot.startDate));
  const { _ } = useTranslate();

  return (
    <Container>
      <Header>
        <Heading>
          <Markdown>{timeslot.title}</Markdown>
        </Heading>
        {timeslot.ticket && (
          <Action>
            <Button onClick={() => setPopupVisible(true)}>
              {_('Show ticket')}
            </Button>
          </Action>
        )}

        {!timeslot.ticket &&
          !isInPast &&
          timeslot.requireRegistration &&
          timeslot.availableSeats > 0 && (
            <Action>
              <Checkbox
                checked={isChecked}
                onClick={toggleIsChecked}
                variant="hero"
              ></Checkbox>
            </Action>
          )}
      </Header>

      {timeslot.ticket && (
        <EventPageTicketPopup
          visible={popupVisible}
          ticket={timeslot.ticket}
          close={() => setPopupVisible(false)}
        />
      )}

      <DetailsContainer>
        {timeslot.requireRegistration &&
          (timeslot.availableSeats > 0 ? (
            <Detail $variant="primary">
              {_('SEATS AVAILABLE')}: {timeslot.availableSeats}
            </Detail>
          ) : (
            <Detail $variant="secondary">{_('FULL')}</Detail>
          ))}

        {timeslot.location && <Detail>{timeslot.location}</Detail>}
      </DetailsContainer>
    </Container>
  );
};
